// Question data structure and branching logic

export const questions = [
  // Q1: Innovation Type (need to know what we're dealing with)
  {
    id: 'q1',
    text: 'What type of innovation are you protecting?',
    options: [
      'Software/algorithm',
      'Hardware/physical product',
      'Manufacturing process or method',
      'Biotech/pharmaceutical',
      'Business method or system',
      'Design (appearance/aesthetics)'
    ],
    tooltip: 'Different innovation types have different IP protection options'
  },
  
  // Q2: Disclosure Status (HARD BLOCKER - eliminates trade secret immediately)
  {
    id: 'q2',
    text: 'Have you publicly disclosed this innovation anywhere?',
    options: [
      'No, completely confidential',
      'Yes, at a conference or publication',
      'Yes, on our website or in marketing',
      'Yes, in customer demos or sales',
      'Not yet, but planning to soon'
    ],
    tooltip: 'Public disclosure starts the 1-year patent clock in US, immediate bar in many countries. Select all that apply.',
    multiSelect: true
  },
  
  // Q3: Budget (HARD BLOCKER - eliminates patent if too low)
  {
    id: 'q3',
    text: 'What\'s your realistic budget for IP protection in the next 12 months?',
    options: [
      'Under $1,000',
      '$1,000-$2,000',
      '$2,000-$5,000',
      '$5,000-$10,000',
      '$10,000+'
    ],
    tooltip: 'Provisional patents start around $2,500, full patents cost $8,000-$15,000+'
  },
  
  // Q4: Funding Plans (affects urgency and investor expectations)
  {
    id: 'q4',
    text: 'What\'s your funding situation?',
    options: [
      'Bootstrapped/self-funded',
      'Planning to raise VC/angel investment',
      'Already raised seed/Series A',
      'Government grants or non-dilutive funding',
      'Not looking for outside capital'
    ],
    tooltip: 'Investors often expect patent filings or IP strategy'
  },
  
  // Q5: Stage (context for timeline)
  {
    id: 'q5',
    text: 'What stage is your company at?',
    options: [
      'Pre-launch/idea stage',
      'Early prototype/MVP',
      'Live product with early customers',
      'Established product with revenue',
      'Planning to raise capital soon'
    ],
    tooltip: 'Your stage affects timing and budget for IP protection'
  },
  
  // Q6: Goals (helps understand priorities)
  {
    id: 'q6',
    text: 'What are your primary goals for IP protection?',
    options: [
      'Block competitors from copying',
      'Attract investors/increase valuation',
      'Generate licensing revenue',
      'Build defensive portfolio',
      'Enable partnerships or acquisitions'
    ],
    tooltip: 'Select all that apply',
    multiSelect: true
  },

  // SOFTWARE PATH - Technical Questions
  {
    id: 'q7a',
    text: 'Where does your software primarily run?',
    options: [
      'Client-side (in user\'s browser/device)',
      'Server-side (hidden from users)',
      'Hybrid (both client and server)',
      'Embedded in hardware'
    ],
    tooltip: 'Client-side code is generally easier to reverse-engineer than server-side',
    branch: 'software'
  },
  {
    id: 'q8a',
    text: 'How easily could a competitor reverse-engineer your innovation?',
    options: [
      'Very easily - it\'s visible in the UI or code',
      'Moderately - requires some technical analysis',
      'Difficult - heavily obfuscated or encrypted',
      'Nearly impossible - proprietary algorithms/processes'
    ],
    tooltip: 'Easier reverse-engineering favors patents over trade secrets',
    branch: 'software'
  },
  {
    id: 'q9a',
    text: 'If a competitor copied your innovation, could you detect it?',
    options: [
      'Yes, easily - it would be visible in their product',
      'Maybe - would require investigation',
      'Unlikely - runs entirely on their infrastructure',
      'Impossible - it\'s internal process only'
    ],
    tooltip: 'You can enforce patents more easily if you can detect infringement',
    branch: 'software'
  },

  // HARDWARE PATH - Technical Questions
  {
    id: 'q7b',
    text: 'How visible is your innovation in the final product?',
    options: [
      'Fully visible - external design/features',
      'Partially visible - requires disassembly',
      'Hidden - internal components only',
      'Combination of visible and hidden elements'
    ],
    tooltip: 'Visible innovations are easier to copy and enforce',
    branch: 'hardware'
  },
  {
    id: 'q8b',
    text: 'How complex is your manufacturing process?',
    options: [
      'Simple - can be made with standard equipment',
      'Moderate - requires some specialized tools/processes',
      'Complex - proprietary manufacturing techniques',
      'Very complex - years to replicate process'
    ],
    tooltip: 'Complex processes can be protected as trade secrets',
    branch: 'hardware'
  },
  {
    id: 'q9b',
    text: 'How difficult would it be for competitors to source similar components?',
    options: [
      'Easy - all standard/off-the-shelf parts',
      'Moderate - some custom components',
      'Difficult - proprietary or exclusive parts',
      'Very difficult - vertically integrated supply chain'
    ],
    tooltip: 'Proprietary components add protection beyond patents',
    branch: 'hardware'
  },

  // PROCESS PATH - Technical Questions
  {
    id: 'q7c',
    text: 'Can competitors observe your process?',
    options: [
      'Yes - it\'s customer-facing or otherwise accessible',
      'Partially - some steps are visible',
      'No - entirely internal operations',
      'Mixed - varies by step'
    ],
    tooltip: 'Observable processes are harder to keep as trade secrets',
    branch: 'process'
  },
  {
    id: 'q8c',
    text: 'How complex is your process to replicate?',
    options: [
      'Simple - industry standard approach',
      'Moderate - requires some expertise',
      'Complex - years of optimization',
      'Extremely complex - near impossible to replicate'
    ],
    tooltip: 'Complex or very specific processes may be better as trade secrets than patents',
    branch: 'process'
  }
];

// Branching logic - determines next question based on current answer
export const getNextQuestion = (currentId, answer, allAnswers) => {
  const innovationType = allAnswers['q1'];

  // Universal questions first (same for everyone)
  if (currentId === 'q1') return 'q2'; // Innovation Type → Disclosure
  if (currentId === 'q2') return 'q3'; // Disclosure → Budget
  if (currentId === 'q3') return 'q4'; // Budget → Funding
  if (currentId === 'q4') return 'q5'; // Funding → Stage
  if (currentId === 'q5') return 'q6'; // Stage → Goals
  
  // After Q6, branch based on innovation type
  if (currentId === 'q6') {
    if (innovationType === 'Software/algorithm') return 'q7a';
    if (innovationType === 'Hardware/physical product') return 'q7b';
    if (innovationType === 'Manufacturing process or method') return 'q7c';
    // Biotech, Business Method, Design skip straight to results (simplified paths)
    return 'RESULTS';
  }

  // SOFTWARE PATH (q7a → q8a → q9a → RESULTS)
  if (currentId === 'q7a') {
    // If server-side, skip reverse-engineering question
    if (answer === 'Server-side (hidden from users)') return 'q9a';
    return 'q8a';
  }
  if (currentId === 'q8a') return 'q9a';
  if (currentId === 'q9a') return 'RESULTS';

  // HARDWARE PATH (q7b → q8b → q9b → RESULTS)
  if (currentId === 'q7b') return 'q8b';
  if (currentId === 'q8b') return 'q9b';
  if (currentId === 'q9b') return 'RESULTS';

  // PROCESS PATH (q7c → q8c → RESULTS)
  if (currentId === 'q7c') return 'q8c';
  if (currentId === 'q8c') return 'RESULTS';

  return 'RESULTS';
};

// TRUE DECISION TREE - Follow logical branches, not scoring
export const calculateRecommendation = (answers) => {
  // Extract key answers (using new question IDs)
  const innovationType = answers['q1'];
  const disclosure = answers['q2'];
  const budget = answers['q3'];
  const funding = answers['q4'];
  const stage = answers['q5'];
  const goals = answers['q6'];
  
  // Helper: Check if disclosed
  const hasDisclosure = Array.isArray(disclosure) 
    ? disclosure.some(d => d && d.includes('Yes'))
    : disclosure && disclosure.includes('Yes');
  
  // Helper: Budget level
  const getBudgetLevel = () => {
    if (!budget) return 0;
    if (budget.includes('Under $1,000')) return 1000;
    if (budget.includes('$1,000-$2,000')) return 2000;
    if (budget.includes('$2,000-$5,000')) return 5000;
    if (budget.includes('$5,000-$10,000')) return 10000;
    if (budget.includes('$10,000+')) return 15000;
    return 0;
  };
  
  const budgetLevel = getBudgetLevel();
  const raisingCapital = funding && (funding.includes('raise') || funding.includes('raised'));
  const earlyStage = stage && stage.includes('Pre-launch');
  
  // HARD RULE 1: If disclosed, trade secret is DEAD
  if (hasDisclosure) {
    // Can only do patent, defensive publication, or ship-it
    if (budgetLevel >= 2000 && raisingCapital) {
      return buildPatentRecommendation(answers, hasDisclosure, 'disclosed_and_raising');
    } else if (budgetLevel >= 2000) {
      return buildPatentRecommendation(answers, hasDisclosure, 'disclosed_with_budget');
    } else if (budgetLevel < 1000 && earlyStage) {
      return buildShipItRecommendation(answers, 'early_and_broke');
    } else {
      return buildDefensivePublicationRecommendation(answers, 'disclosed_low_budget');
    }
  }
  
  // HARD RULE 2: Very low budget + early stage = Ship-it
  if (budgetLevel < 1000 && earlyStage) {
    return buildShipItRecommendation(answers, 'very_early');
  }
  
  // Branch by innovation type
  if (innovationType === 'Software/algorithm') {
    return softwareDecisionTree(answers, budgetLevel, raisingCapital);
  } else if (innovationType === 'Hardware/physical product') {
    return hardwareDecisionTree(answers, budgetLevel, raisingCapital);
  } else if (innovationType === 'Manufacturing process or method') {
    return processDecisionTree(answers, budgetLevel, raisingCapital);
  } else if (innovationType === 'Business method or system') {
    return businessMethodDecisionTree(answers, budgetLevel, raisingCapital);
  } else if (innovationType === 'Biotech/pharmaceutical') {
    return biotechDecisionTree(answers, budgetLevel, raisingCapital);
  } else if (innovationType === 'Design (appearance/aesthetics)') {
    return designDecisionTree(answers, budgetLevel, raisingCapital);
  }
  
  // Fallback
  return buildPatentRecommendation(answers, false, 'default');
};

// SOFTWARE DECISION TREE
function softwareDecisionTree(answers, budgetLevel, raisingCapital) {
  const execution = answers['q7a'];
  const reverseEngineering = answers['q8a'];
  const detection = answers['q9a'];
  
  // Client-side = visible/reverse-engineerable → Patent
  if (execution && execution.includes('Client-side')) {
    if (budgetLevel >= 2000 || raisingCapital) {
      return buildPatentRecommendation(answers, false, 'software_visible');
    } else {
      return buildShipItRecommendation(answers, 'visible_no_budget');
    }
  }
  
  // Server-side = hidden → Evaluate detectability
  if (execution && execution.includes('Server-side')) {
    // Can't detect infringement = Can't enforce patent → Trade secret
    if (detection && (detection.includes('Impossible') || detection.includes('Unlikely'))) {
      return buildTradeSecretRecommendation(answers, 'undetectable');
    }
    
    // Detectable + raising capital → Patent (investors want it)
    if (raisingCapital && budgetLevel >= 2000) {
      return buildPatentRecommendation(answers, false, 'raising_capital');
    }
    
    // Detectable but not raising capital → Trade secret is viable
    if (budgetLevel < 2000) {
      return buildTradeSecretRecommendation(answers, 'server_side_viable');
    }
    
    // Default: Patent if budget allows
    return budgetLevel >= 2000 
      ? buildPatentRecommendation(answers, false, 'server_side_budget')
      : buildTradeSecretRecommendation(answers, 'server_side_no_budget');
  }
  
  // Hybrid or Embedded → Evaluate reverse engineering difficulty
  if (reverseEngineering) {
    if (reverseEngineering.includes('Very easily') || reverseEngineering.includes('Moderately')) {
      return budgetLevel >= 2000
        ? buildPatentRecommendation(answers, false, 'easy_reverse_engineer')
        : buildDefensivePublicationRecommendation(answers, 'easy_reverse_no_budget');
    } else {
      // Difficult to reverse engineer → Trade secret viable
      return buildTradeSecretRecommendation(answers, 'difficult_reverse_engineer');
    }
  }
  
  // Default
  return buildPatentRecommendation(answers, false, 'software_default');
}

// HARDWARE DECISION TREE
function hardwareDecisionTree(answers, budgetLevel, raisingCapital) {
  const visibility = answers['q7b'];
  const manufacturing = answers['q8b'];
  const components = answers['q9b'];
  
  // Fully visible = easy to copy → Patent
  if (visibility && visibility.includes('Fully visible')) {
    return budgetLevel >= 2000
      ? buildPatentRecommendation(answers, false, 'hardware_visible')
      : buildShipItRecommendation(answers, 'visible_no_budget');
  }
  
  // Hidden + complex manufacturing → Trade secret viable
  if (visibility && visibility.includes('Hidden')) {
    if (manufacturing && (manufacturing.includes('Complex') || manufacturing.includes('Very complex'))) {
      return buildTradeSecretRecommendation(answers, 'hidden_complex');
    }
  }
  
  // Partially visible + raising capital → Patent
  if (raisingCapital && budgetLevel >= 2000) {
    return buildPatentRecommendation(answers, false, 'raising_capital');
  }
  
  // Default: Patent if budget allows
  return budgetLevel >= 2000
    ? buildPatentRecommendation(answers, false, 'hardware_default')
    : buildShipItRecommendation(answers, 'no_budget');
}

// PROCESS DECISION TREE
function processDecisionTree(answers, budgetLevel, raisingCapital) {
  const observable = answers['q7c'];
  const complexity = answers['q8c'];
  
  // Observable process → Patent (can't keep secret)
  if (observable && observable.includes('Yes')) {
    return budgetLevel >= 2000
      ? buildPatentRecommendation(answers, false, 'observable_process')
      : buildDefensivePublicationRecommendation(answers, 'observable_no_budget');
  }
  
  // Internal + complex → Trade secret
  if (observable && observable.includes('No')) {
    if (complexity && (complexity.includes('Complex') || complexity.includes('Extremely complex'))) {
      return buildTradeSecretRecommendation(answers, 'internal_complex_process');
    }
    
    // Internal but not complex → Patent if budget and raising capital
    if (raisingCapital && budgetLevel >= 2000) {
      return buildPatentRecommendation(answers, false, 'raising_capital');
    }
    
    return buildTradeSecretRecommendation(answers, 'internal_process');
  }
  
  // Default
  return buildPatentRecommendation(answers, false, 'process_default');
}

// BUSINESS METHOD DECISION TREE
function businessMethodDecisionTree(answers, budgetLevel, raisingCapital) {
  // Business methods face Alice issues
  if (budgetLevel >= 5000 && raisingCapital) {
    // Can try patent but with big warning
    return buildPatentRecommendation(answers, false, 'business_method_risky');
  } else {
    // Lean toward trade secret
    return buildTradeSecretRecommendation(answers, 'business_method');
  }
}

// BIOTECH DECISION TREE
function biotechDecisionTree(answers, budgetLevel, raisingCapital) {
  // Biotech usually needs patent for regulatory/licensing
  if (budgetLevel >= 5000 || raisingCapital) {
    return buildPatentRecommendation(answers, false, 'biotech');
  } else {
    return buildShipItRecommendation(answers, 'biotech_no_budget');
  }
}

// DESIGN DECISION TREE
function designDecisionTree(answers, budgetLevel, raisingCapital) {
  // Design patents are different
  if (budgetLevel >= 2000 || raisingCapital) {
    return buildPatentRecommendation(answers, false, 'design');
  } else {
    return buildShipItRecommendation(answers, 'design_no_budget');
  }
}

// RECOMMENDATION BUILDERS
function buildPatentRecommendation(answers, hasDisclosure, reason) {
  const innovationType = answers['q1'];
  const funding = answers['q4'];
  
  let strategy = 'patent';
  let title = 'Patent Protection';
  let reasoning = [
    'Your innovation is likely to be reverse-engineered or visible to competitors',
    'Patent protection gives you exclusive rights to enforce',
    'Having "patent pending" status helps with investors and partnerships'
  ];
  
  if (funding && funding.includes('raise')) {
    reasoning.push('Investors typically expect patent filings or a clear IP strategy');
  }
  
  let nextSteps = [
    'Document your invention thoroughly with technical details',
    'File a provisional patent application within 30-60 days',
    'Consider working with Patentext to generate a high-quality draft',
    'Plan to convert to non-provisional within 12 months'
  ];
  
  let costEstimate = '$2,500-$5,000 (provisional) or $400-600 (DIY with Patentext)';
  let timeline = 'File provisional ASAP, convert within 12 months';
  
  let warnings = [
    'Patent process takes 2-3 years for full grant',
    'Filing costs add up (provisional → non-provisional → prosecution)'
  ];
  
  if (hasDisclosure) {
    warnings.unshift('You\'ve already publicly disclosed - you may have limited time to file (1 year in US, none in most other countries)');
  }
  
  // Business Method Alice warning
  if (innovationType === 'Business method or system') {
    warnings.unshift('Business methods face strict patentability requirements under Alice v. CLS Bank. Consult with a patent attorney about §101 eligibility before filing.');
  }
  
  // Biotech consultation
  if (innovationType === 'Biotech/pharmaceutical') {
    reasoning.push('Biotech innovations have complex considerations including regulatory data exclusivity.');
    warnings.push('We recommend consulting with a patent attorney who specializes in biotech and pharmaceutical patents.');
  }
  
  // Design patent note
  if (innovationType === 'Design (appearance/aesthetics)') {
    reasoning.push('Design innovations may be protected by design patents, which have different requirements than utility patents.');
    warnings.push('We recommend consulting with a patent attorney about whether a design patent or utility patent is more appropriate.');
  }
  
  return { strategy, title, reasoning, nextSteps, costEstimate, timeline, warnings };
}

function buildTradeSecretRecommendation(answers, reason) {
  const innovationType = answers['q1'];
  
  let strategy = 'trade-secret';
  let title = 'Trade Secret Protection';
  let reasoning = [
    'Your innovation runs server-side or has hidden complexity',
    'Infringement would be difficult or impossible to detect',
    'Trade secrets don\'t expire and provide indefinite protection'
  ];
  
  let nextSteps = [
    'Implement strict NDAs for all employees and contractors',
    'Document trade secret policies and access controls',
    'Use password protection and encryption for critical information',
    'Train team on confidentiality requirements'
  ];
  
  let costEstimate = '$500-$2,000 (for legal policies and NDAs)';
  let timeline = 'Implement protections within 30 days';
  let warnings = [
    'If your innovation is reverse-engineered or independently discovered, you lose protection',
    'Requires ongoing vigilance and employee management',
    'Consult with an attorney specializing in trade secrets to determine reasonable protections'
  ];
  
  return { strategy, title, reasoning, nextSteps, costEstimate, timeline, warnings };
}

function buildDefensivePublicationRecommendation(answers, reason) {
  let strategy = 'defensive-publication';
  let title = 'Defensive Publication';
  let reasoning = [
    'You want to block competitors without patent costs',
    'Your budget is limited for patent filing',
    'Publishing creates prior art that prevents others from patenting'
  ];
  
  let nextSteps = [
    'Write a detailed technical disclosure of your innovation',
    'Publish in a recognized prior art database (IP.com, Research Disclosure)',
    'Continue iterating and publishing new developments',
    'Monitor competitor patent applications'
  ];
  
  let costEstimate = '$0-$500 for publication';
  let timeline = 'Publish within 1-2 weeks';
  let warnings = [
    'You permanently give up the right to patent this innovation in most jurisdictions outside of the US, and you start a 1-year clock to file a patent application in the US',
    'Limits future licensing revenue and enforcement rights'
  ];
  
  return { strategy, title, reasoning, nextSteps, costEstimate, timeline, warnings };
}

function buildShipItRecommendation(answers, reason) {
  let strategy = 'ship-it';
  let title = 'Focus on Execution, Revisit IP Later';
  let reasoning = [
    'You\'re very early stage - speed to market is critical',
    'Your budget is limited for IP protection right now',
    'First-mover advantage and execution matter more than IP at this stage'
  ];
  
  let nextSteps = [
    'Document everything in dated invention notebooks',
    'Use NDAs with all employees, contractors, and early partners',
    'Avoid public disclosure if possible to preserve patent rights',
    'Set a calendar reminder to reassess IP strategy in 6 months'
  ];
  
  let costEstimate = '$0 now, revisit when you have $2K+ budget';
  let timeline = 'Reassess in 6 months or after key milestones';
  let warnings = [
    'Public disclosure starts a 1-year clock in the US (immediate bar in other countries)',
    'Document thoroughly now even if you\'re not filing yet',
    'Beware of the on-sale bar: offers for sale of a technology, even under NDA, are considered public disclosure'
  ];
  
  return { strategy, title, reasoning, nextSteps, costEstimate, timeline, warnings };
}

